import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import ButtonLoading from '@/components/common/ButtonLoading';
import {
    useCheckAuthState,
    useToastNotification,
    useDocumentFormData,
    useSubscription
} from '@/hooks';
import { useSaveReceipt, useValidateReceipt } from '@/hooks/useSidebarActions';
import { SaveIcon } from '@/resources/icons';
import { documentSaved as posthogDocumentSaved } from '@/modules/posthog/posthogManager';

import useStyles from '../../styles';

const ReceiptSave = ({ onNegativeReceipt, onLoginBlocker }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);

    const { loadingUser, registeredUser } = useCheckAuthState();
    const saveReceipt = useSaveReceipt();
    const { documentLoading, total, locked } = useDocumentFormData();
    const validateReceipt = useValidateReceipt();
    const { triggerToast } = useToastNotification();
    const subscriptionData = useSubscription();

    // Used for validation.
    const receipt = useSelector((state) => state?.makeInvoice);

    const subInfo = subscriptionData?.components?.document;
    const blockDocumentCreation =
        !subInfo?.enabled || (subInfo?.limited && subInfo?.amountRemaining < 1);

    const handleOnClick = async () => {
        setLoading(true);
        const receiptData = await validateReceipt(receipt);

        if (!receiptData) {
            setLoading(false);
            return;
        }

        if (total < 0) {
            onNegativeReceipt();
            setLoading(false);
            return;
        }

        const completeSaveProcess = async () => {
            await saveReceipt(receiptData, 'save');

            if (!blockDocumentCreation) {
                triggerToast({
                    message: 'Receipt Saved',
                    action: 'saved',
                    icon: <SaveIcon />
                });
            }

            posthogDocumentSaved(receiptData);
            setLoading(false);
        };

        // Check is a user is registered, if they are complete the process.
        // If not, see if we can get them to register.
        if (registeredUser) {
            completeSaveProcess();
        } else {
            onLoginBlocker(completeSaveProcess);
        }
    };

    const loadingButton = loadingUser || documentLoading;

    if (!locked) {
        return (
            <div className={classes.section}>
                {!loadingButton ? (
                    <ButtonLoading
                        fullWidth
                        className={classes.button}
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<SaveIcon className={classes.buttonSaveSvg} />}
                        onClick={handleOnClick}
                        disableOnLoading
                        loading={loading}
                    >
                        Save
                    </ButtonLoading>
                ) : (
                    <Skeleton variant="rectangular" className={classes.sizer} />
                )}
            </div>
        );
    }
    return null;
};

ReceiptSave.propTypes = {
    onNegativeReceipt: PropTypes.func.isRequired,
    onLoginBlocker: PropTypes.func.isRequired
};

export default ReceiptSave;
